// components/common/HelpTip.jsx
import React, { useEffect, useRef, useState } from "react";
import { useClickOutSide } from "lib/hooks/useClickOutside";
import { getHelpTooltip } from "lib/api/getHelpTooltip";
import { useTranslation } from "react-i18next";

export default function HelpTip({
  tipKey, // ✅ 필수
  placement = "right",
  className = "",
  maxWidth = 500,
}) {
  const { t, i18n } = useTranslation();
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const bubbleRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  useClickOutSide([wrapRef, btnRef, bubbleRef], () => {
    if (open) setOpen(false);
  });

  useEffect(() => {
    setLoaded(false);
    setTitle("");
    setContent("");

    if (!tipKey) {
      setLoaded(true);
      return;
    }

    let alive = true;

    (async () => {
      try {
        const locale = i18n.resolvedLanguage || i18n.language || "ko-KR";
        const res = await getHelpTooltip(tipKey, { locale });
        const tooltip = res?.data?.tooltip;

        if (!alive) return;

        setTitle(tooltip?.title || "");
        setContent(tooltip?.content || "");
        setLoaded(true);
      } catch (e) {
        if (!alive) return;
        setLoaded(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, [tipKey, i18n.language, i18n.resolvedLanguage]);

  const isEmpty = !title && !content;
  const displayTitle = title || t("helpTip.title");
  const displayContent = !loaded
    ? t("helpTip.loading")
    : isEmpty
      ? t("helpTip.noContent")
      : content;
  const stateClass = !loaded ? "isLoading" : isEmpty ? "isEmpty" : "isReady";

  return (
    <span
      title={t("helpTip.title")}
      ref={wrapRef}
      className={`helpTipWrap ${className}`}
    >
      <button
        ref={btnRef}
        type="button"
        className="helpTipBtn"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("helpTip.ariaLabel")}
        aria-expanded={open ? "true" : "false"}
      >
        ?
      </button>

      {open ? (
        <div
          ref={bubbleRef}
          className={`helpTipBubble place-${placement} ${stateClass}`}
          style={{ maxWidth }}
          role="tooltip"
        >
          <div className="helpTipHeader">
            <span className="helpTipBadge" aria-hidden="true">
              i
            </span>
            <div className="helpTipTitleWrap">
              <div className="helpTipTitle">{displayTitle}</div>
              <div className="helpTipSubText">{t("helpTip.title")}</div>
            </div>
          </div>
          <div className="helpTipDivider" />
          <div className="helpTipBody">
            <div className="helpTipContent">{displayContent}</div>
          </div>
        </div>
      ) : null}
    </span>
  );
}
